<template>
  <div class="bg-white rounded-lg shadow hover:shadow-md transition-shadow overflow-hidden">
    <!-- Product Image -->
    <div class="relative h-48 bg-gray-200 overflow-hidden">
      <img 
        v-if="listing.images && listing.images.length > 0"
        :src="listing.images[0]" 
        :alt="listing.title"
        class="w-full h-full object-cover"
      />
      <div v-else class="w-full h-full flex items-center justify-center">
        <span class="material-symbols-outlined text-6xl text-gray-400">image</span>
      </div>
      
      <!-- Status Badge -->
      <div v-if="listing.status === 'sold'" class="absolute top-2 right-2 bg-red-500 text-white px-3 py-1 rounded-full text-xs font-semibold">
        SOLD
      </div>
      <div v-else-if="isMyListing" class="absolute top-2 right-2 bg-blue-900 text-white px-3 py-1 rounded-full text-xs font-semibold">
        Your Listing
      </div>
    </div>
    
    <!-- Product Details -->
    <div class="p-4">
      <div class="flex items-start justify-between mb-2">
        <h3 class="font-semibold text-lg text-gray-900 line-clamp-1">{{ listing.title }}</h3>
        <span class="text-lg font-bold text-blue-900 ml-2 whitespace-nowrap">R{{ listing.price }}</span>
      </div>
      
      <p class="text-sm text-gray-600 line-clamp-2 mb-3">{{ listing.description }}</p>
      
      <!-- Seller Info with Profile Picture - Clickable -->
      <div class="flex items-center justify-between text-sm mb-3">
        <div 
          class="flex items-center text-gray-600 cursor-pointer hover:text-blue-700 transition-colors"
          @click="viewSellerProfile"
        >
          <!-- Seller Profile Picture -->
          <div class="w-6 h-6 rounded-full overflow-hidden mr-2 bg-gray-300 flex-shrink-0">
            <img 
              v-if="listing.sellerProfilePic" 
              :src="listing.sellerProfilePic" 
              :alt="listing.sellerName"
              class="w-full h-full object-cover"
            />
            <span v-else class="material-symbols-outlined text-sm text-gray-500 flex items-center justify-center h-full">
              person
            </span>
          </div>
          <span class="truncate hover:underline">{{ listing.sellerName }}</span>
        </div>
        <span class="text-xs text-gray-400">
          {{ formatDate(listing.createdAt) }}
        </span>
      </div>
      
      <!-- Category Badge -->
      <div class="mb-3">
        <span :class="['inline-block px-2 py-1 rounded-full text-xs font-medium', getCategoryColor(listing.category)]">
          {{ listing.category }}
        </span>
      </div>
      
      <!-- Action Buttons -->
      <div class="flex gap-2">
        <!-- Contact Seller Button - Show for non-owners or in browse mode -->
        <button 
          v-if="(!isMyListing || showContactForOwn) && listing.status === 'active'"
          @click="$emit('contact', listing)"
          class="flex-1 px-4 py-2 bg-blue-900 text-white rounded-lg hover:bg-blue-800 transition-colors flex items-center justify-center"
        >
          <span class="material-symbols-outlined mr-1 text-sm">chat</span>
          Contact Seller
        </button>
        
        <!-- My Listing Actions (only in My Listings tab - when showContactForOwn is false) -->
        <template v-if="isMyListing && !showContactForOwn">
          <button 
            v-if="listing.status === 'active'"
            @click="$emit('mark-sold', listing)"
            class="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm"
          >
            Mark as Sold
          </button>
          <button 
            @click="$emit('edit', listing)"
            class="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <span class="material-symbols-outlined text-sm">edit</span>
          </button>
          <button 
            @click="$emit('delete', listing)"
            class="px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition-colors"
          >
            <span class="material-symbols-outlined text-sm">delete</span>
          </button>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    listing: {
      type: Object,
      required: true
    },
    currentUserId: {
      type: String,
      default: ''
    },
    showContactForOwn: {
      type: Boolean,
      default: false
    }
  },
  emits: ['contact', 'edit', 'delete', 'mark-sold'],
  computed: {
    isMyListing() {
      return this.currentUserId === this.listing.sellerId;
    }
  },
  methods: {
    formatDate(timestamp) {
      if (!timestamp) return '';
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      const now = new Date();
      const diffTime = Math.abs(now - date);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      if (diffDays === 0) return 'Today';
      if (diffDays === 1) return 'Yesterday';
      if (diffDays < 7) return `${diffDays} days ago`;
      
      return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
    },
    
    getCategoryColor(category) {
      const colors = {
        'Books': 'bg-blue-100 text-blue-800',
        'Electronics': 'bg-purple-100 text-purple-800',
        'Clothing': 'bg-pink-100 text-pink-800',
        'Furniture': 'bg-yellow-100 text-yellow-800',
        'Stationery': 'bg-green-100 text-green-800',
        'Other': 'bg-gray-100 text-gray-800'
      };
      return colors[category] || 'bg-gray-100 text-gray-800';
    },
    
    viewSellerProfile() {
      // Navigate to the seller's profile page
      this.$router.push(`/user/${this.listing.sellerId}`);
    }
  }
};
</script>
